<template>
  <div class="image-loader">
    <div class="gradient-bg" :class="{ loaded: imageLoaded, error: imageError }"></div>

    <!-- Error state -->
    <div v-if="imageError" class="error-state">
      <div class="error-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          class="error-icon"
        >
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3zM12 17l-5-5m0 5l5-5"
          />
        </svg>
        <h3 class="error-title">{{ errorTitle || $t('photo.image_failed_title') }}</h3>
        <p class="error-message">{{ errorMessage || $t('photo.image_failed_message') }}</p>
        <button @click="retryImageLoad" class="retry-button">
          {{ retryButtonText || $t('photo.retry_load') }}
        </button>
      </div>
    </div>

    <!-- Images (hidden when error) -->
    <template v-else>
      <img
        :src="imageUrl"
        :alt="imageLoaded ? alt : ''"
        :loading="loading"
        :class="['main-image', imageClass, { loaded: imageLoaded }]"
        @load="onImageLoad"
        @error="onImageError"
      />
      <img
        :src="imageUrl"
        :class="['blur-image', imageClass, { loaded: imageLoaded }]"
        @error="onImageError"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  src: string
  alt?: string
  loading?: 'eager' | 'lazy'
  imageClass?: string
  errorTitle?: string
  errorMessage?: string
  retryButtonText?: string
  maxRetries?: number
}>()

const emit = defineEmits<{
  load: []
  error: []
}>()

const imageLoaded = ref(false)
const imageError = ref(false)
const retryCount = ref(0)

// Create a computed property for the image URL with cache busting on retry
const imageUrl = computed(() => {
  if (retryCount.value === 0) {
    return props.src
  }
  // Add cache-busting parameter for retries
  const url = new URL(props.src)
  url.searchParams.set('retry', retryCount.value.toString())
  return url.toString()
})

const onImageLoad = () => {
  imageLoaded.value = true
  imageError.value = false
  emit('load')
}

const onImageError = () => {
  console.error('Failed to load image:', props.src)
  imageError.value = true
  imageLoaded.value = false
  emit('error')
}

const retryImageLoad = () => {
  const maxRetries = props.maxRetries ?? 3
  if (retryCount.value < maxRetries) {
    // Limit retries to prevent infinite loops
    retryCount.value++
    imageError.value = false
    imageLoaded.value = false
  }
}

// Reset state when src changes
watch(() => props.src, () => {
  imageLoaded.value = false
  imageError.value = false
  retryCount.value = 0
})
</script>

<style scoped>
.image-loader {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.gradient-bg {
  position: absolute;
  inset: 0;
  background: var(--gradient-bg);
  background-size: 200% 200%;
  animation: gradient 3s ease infinite;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
}

.gradient-bg.loaded {
  opacity: 0;
}

.gradient-bg.error {
  background: theme('colors.stone.100');
  animation: none;

  .dark & {
    background: theme('colors.stone.800');
  }
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.main-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.main-image.loaded {
  opacity: 1;
}

.blur-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  filter: blur(20px);
  transform: scale(1.1);
  transition: opacity 0.5s ease-in-out;
}

.blur-image.loaded {
  opacity: 0;
}

.error-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: theme('colors.stone.50');

  .dark & {
    background: theme('colors.stone.900');
  }
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: theme('spacing.4');
  text-align: center;
  padding: theme('spacing.6');
  max-width: 300px;
}

.error-icon {
  color: theme('colors.stone.400');

  .dark & {
    color: theme('colors.stone.500');
  }
}

.error-title {
  font-size: theme('fontSize.lg');
  font-weight: theme('fontWeight.semibold');
  color: theme('colors.stone.900');
  margin: 0;

  .dark & {
    color: theme('colors.stone.100');
  }
}

.error-message {
  font-size: theme('fontSize.sm');
  color: theme('colors.stone.600');
  margin: 0;

  .dark & {
    color: theme('colors.stone.400');
  }
}

.retry-button {
  background: theme('colors.blue.600');
  color: white;
  border: none;
  border-radius: theme('borderRadius.md');
  padding: theme('spacing.2') theme('spacing.4');
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.medium');
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: theme('colors.blue.700');
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px theme('colors.blue.200');
  }

  .dark &:focus {
    box-shadow: 0 0 0 3px theme('colors.blue.800');
  }
}
</style>

